import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ReceiptsDashboard.css';
import { formatDateShortRD, formatDateRD, formatDateTimeRD } from '../utils/dateUtils';

const ReceiptsDashboard = ({ isOpen, onClose, onOpenChat, standalone = false }) => {
  const getTodayLocal = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
  };
  const [activeTab, setActiveTab] = useState('summary'); // summary, daily, monthly, pending, auto-applied
  const [summary, setSummary] = useState(null);
  const [dailyStats, setDailyStats] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [pendingReceipts, setPendingReceipts] = useState([]);
  const [autoAppliedReceipts, setAutoAppliedReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getTodayLocal());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showRegisterPayment, setShowRegisterPayment] = useState(false);
  const [applyingReceipt, setApplyingReceipt] = useState(null); // receipt being manually applied
  const [applyPaymentData, setApplyPaymentData] = useState({ amount: '', bank: 'BANRESERVAS' });
  const [registerPaymentData, setRegisterPaymentData] = useState({
    phoneNumber: '',
    amount: '',
    bank: 'BANRESERVAS',
    type: 'transferencia',
    date: new Date().toISOString().split('T')[0],
    time: '',
    transactionNumber: '',
    notes: ''
  });

  useEffect(() => {
    if (isOpen || standalone) {
      loadSummary();
      if (activeTab === 'daily') loadDailyStats();
      if (activeTab === 'monthly') loadMonthlyStats();
      if (activeTab === 'pending') loadPendingReceipts();
      if (activeTab === 'auto-applied') loadAutoAppliedReceipts();
    }
  }, [isOpen, standalone, activeTab, selectedDate, selectedMonth, selectedYear]);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/receipts/stats/summary', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        setSummary(response.data.summary);
      }
    } catch (error) {
      console.error('Error cargando resumen:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDailyStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/receipts/stats/daily?date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        setDailyStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error cargando estadísticas diarias:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlyStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/receipts/stats/monthly?year=${selectedYear}&month=${selectedMonth}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        setMonthlyStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error cargando estadísticas mensuales:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingReceipts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/receipts/pending-review', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        setPendingReceipts(response.data.receipts);
      }
    } catch (error) {
      console.error('Error cargando recibos pendientes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAutoAppliedReceipts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/receipts/auto-applied?date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        setAutoAppliedReceipts(response.data.receipts);
      }
    } catch (error) {
      console.error('Error cargando recibos auto-aplicados:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusLabel = (status) => {
    const labels = { approved: 'Aprobado', pending: 'Pendiente', rejected: 'Eliminado', duplicate: 'Duplicado', verified: 'Verificado', fake: 'Falso' };
    return labels[status] || status;
  };

  const handleOpenChat = (phoneNumber) => {
    if (onOpenChat) {
      onClose();
      onOpenChat(phoneNumber);
    }
  };

  const handleApplyPending = async (receipt) => {
    const amount = parseFloat(applyPaymentData.amount) || receipt.amount;
    if (!amount) {
      alert('Ingresa el monto del pago');
      return;
    }
    try {
      setLoading(true);
      const response = await axios.post(`/api/receipts/${receipt._id}/apply-payment`, {
        amount,
        pasarela: applyPaymentData.bank || receipt.bank || 'BANRESERVAS'
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        alert('✅ Pago aplicado exitosamente en Mikrowisp');
        setApplyingReceipt(null);
        setApplyPaymentData({ amount: '', bank: 'BANRESERVAS' });
        loadPendingReceipts();
        loadSummary();
        if (activeTab === 'daily') loadDailyStats();
      } else {
        alert('Error: ' + (response.data.error || 'No se pudo aplicar el pago'));
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (!isOpen && !standalone) return null;

  return (
    <div className={standalone ? "receipts-dashboard-page" : "receipts-dashboard-overlay"} onClick={standalone ? undefined : onClose}>
      <div className="receipts-dashboard" onClick={(e) => e.stopPropagation()}>
        <div className="dashboard-header">
          <h2>📊 Dashboard de Recibos de Pago</h2>
          {!standalone && <button className="close-button" onClick={onClose}>✕</button>}
        </div>

        <div className="dashboard-tabs">
          <button
            className={activeTab === 'summary' ? 'active' : ''}
            onClick={() => setActiveTab('summary')}
          >
            📈 Resumen
          </button>
          <button
            className={activeTab === 'daily' ? 'active' : ''}
            onClick={() => setActiveTab('daily')}
          >
            📅 Diario
          </button>
          <button
            className={activeTab === 'monthly' ? 'active' : ''}
            onClick={() => setActiveTab('monthly')}
          >
            📆 Mensual
          </button>
          <button
            className={activeTab === 'pending' ? 'active' : ''}
            onClick={() => setActiveTab('pending')}
          >
            ⏳ Pendientes ({pendingReceipts.length})
          </button>
          <button
            className={activeTab === 'auto-applied' ? 'active' : ''}
            onClick={() => setActiveTab('auto-applied')}
          >
            🤖 Auto-IA
          </button>
        </div>

        <div className="dashboard-content">
          {loading && <div className="loading">Cargando...</div>}

          {activeTab === 'summary' && summary && (
            <div className="summary-tab">
              <div className="summary-cards">
                <div className="summary-card">
                  <h3>Hoy</h3>
                  <div className="card-content">
                    <div className="stat-item">
                      <span className="stat-label">Total Recibos:</span>
                      <span className="stat-value">{summary.today.total}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Aprobados:</span>
                      <span className="stat-value success">{summary.today.approved}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Pendientes:</span>
                      <span className="stat-value warning">{summary.today.pending}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Auto-IA:</span>
                      <span className="stat-value info">{summary.today.autoApplied}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Monto Total:</span>
                      <span className="stat-value amount">{formatCurrency(summary.today.totalAmount)}</span>
                    </div>
                  </div>
                </div>

                <div className="summary-card">
                  <h3>Este Mes</h3>
                  <div className="card-content">
                    <div className="stat-item">
                      <span className="stat-label">Total Recibos:</span>
                      <span className="stat-value">{summary.thisMonth.total}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Aprobados:</span>
                      <span className="stat-value success">{summary.thisMonth.approved}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Auto-IA:</span>
                      <span className="stat-value info">{summary.thisMonth.autoApplied}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Monto Total:</span>
                      <span className="stat-value amount">{formatCurrency(summary.thisMonth.totalAmount)}</span>
                    </div>
                  </div>
                </div>

                <div className="summary-card">
                  <h3>Mes Pasado</h3>
                  <div className="card-content">
                    <div className="stat-item">
                      <span className="stat-label">Total Recibos:</span>
                      <span className="stat-value">{summary.lastMonth.total}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Aprobados:</span>
                      <span className="stat-value success">{summary.lastMonth.approved}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Monto Total:</span>
                      <span className="stat-value amount">{formatCurrency(summary.lastMonth.totalAmount)}</span>
                    </div>
                  </div>
                </div>

                <div className="summary-card alert">
                  <h3>⚠️ Pendientes de Revisión</h3>
                  <div className="card-content">
                    <div className="stat-item">
                      <span className="stat-value large">{summary.pendingReview}</span>
                    </div>
                    <button
                      className="btn-view-pending"
                      onClick={() => setActiveTab('pending')}
                    >
                      Ver Detalles
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'daily' && (
            <div className="daily-tab">
              <div className="date-selector">
                <label>Fecha:</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
                <button onClick={loadDailyStats}>Actualizar</button>
              </div>

              {dailyStats && (
                <>
                  <div className="daily-summary">
                    <h3>Resumen del Día - {formatDateRD(dailyStats.date)}</h3>
                    <div className="daily-stats-grid">
                      <div className="stat-box">
                        <div className="stat-label">Total Recibos</div>
                        <div className="stat-number">{dailyStats.total}</div>
                      </div>
                      <div className="stat-box success">
                        <div className="stat-label">Aprobados</div>
                        <div className="stat-number">{dailyStats.approved}</div>
                      </div>
                      <div className="stat-box warning">
                        <div className="stat-label">Pendientes</div>
                        <div className="stat-number">{dailyStats.pending}</div>
                      </div>
                      <div className="stat-box info">
                        <div className="stat-label">Auto-IA</div>
                        <div className="stat-number">{dailyStats.autoApplied}</div>
                      </div>
                      <div className="stat-box amount">
                        <div className="stat-label">Monto Total</div>
                        <div className="stat-number">{formatCurrency(dailyStats.totalAmount)}</div>
                      </div>
                    </div>
                  </div>

                  <div className="banks-breakdown">
                    <h3>Desglose por Banco</h3>
                    <div className="banks-grid">
                      {Object.entries(dailyStats.byBank).map(([bank, data]) => (
                        <div key={bank} className="bank-card">
                          <h4>{bank}</h4>
                          <div className="bank-stats">
                            <div className="bank-stat">
                              <span className="label">Cantidad:</span>
                              <span className="value">{data.count}</span>
                            </div>
                            <div className="bank-stat">
                              <span className="label">Monto:</span>
                              <span className="value amount">{formatCurrency(data.amount)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="daily-receipts-list">
                    <h3>Recibos del Día</h3>
                    <table className="receipts-table">
                      <thead>
                        <tr>
                          <th>Hora</th>
                          <th>ID</th>
                          <th>Cliente</th>
                          <th>Banco</th>
                          <th>Tipo</th>
                          <th>Monto</th>
                          <th>Origen</th>
                          <th>Nº Trans.</th>
                          <th>Estado</th>
                          <th>Duplicado de</th>
                          <th>Aplicado por</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dailyStats.receipts.map(receipt => (
                          <tr key={receipt._id} className={receipt.status === 'duplicate' ? 'duplicate-row' : ''}>
                            <td>{formatDateTimeRD(receipt.createdAt, { hour: '2-digit', minute: '2-digit' })}</td>
                            <td>
                              {receipt.contact?.mikrowispId
                                ? <span className="client-id-link" onClick={() => handleOpenChat(receipt.phoneNumber)} title="Abrir chat">{receipt.contact.mikrowispId}</span>
                                : <span className="no-id">-</span>}
                            </td>
                            <td>
                              <span className="client-name-link" onClick={() => handleOpenChat(receipt.phoneNumber)} title="Abrir chat">
                                {receipt.contact?.name || receipt.phoneNumber}
                              </span>
                            </td>
                            <td>{receipt.bank || 'N/A'}</td>
                            <td>{receipt.type || 'N/A'}</td>
                            <td className="amount">{formatCurrency(receipt.amount)}</td>
                            <td>
                              {receipt.accountNumber ? (
                                <span className="origin-value">{receipt.accountNumber}</span>
                              ) : receipt.phoneNumber ? (
                                <span className="origin-value" title="Teléfono WhatsApp que envió el comprobante">{receipt.phoneNumber}</span>
                              ) : <span className="no-id">-</span>}
                            </td>
                            <td className="mono-small">{receipt.transactionNumber || receipt.confirmationNumber || <span className="no-id">-</span>}</td>
                            <td>
                              <span className={`status-badge ${receipt.status}`}>
                                {statusLabel(receipt.status)}
                              </span>
                            </td>
                            <td>
                              {(receipt.status === 'duplicate' || receipt.duplicateOf || receipt.duplicateInfo) ? (
                                <div className="duplicate-of-cell" style={{ fontSize: '12px', lineHeight: 1.35, color: '#92400e' }}>
                                  <div style={{ fontWeight: 600 }}>
                                    ID {receipt.duplicateInfo?.mikrowispId || '—'}
                                  </div>
                                  <div>
                                    Fecha: {receipt.duplicateInfo?.date
                                      ? (String(receipt.duplicateInfo.date).length >= 10
                                          ? String(receipt.duplicateInfo.date).slice(0, 10)
                                          : formatDateShortRD(receipt.duplicateInfo.date))
                                      : (receipt.duplicateInfo?.createdAt
                                          ? formatDateShortRD(receipt.duplicateInfo.createdAt)
                                          : '—')}
                                  </div>
                                  {(receipt.duplicateInfo?.contactName || receipt.duplicateInfo?.phoneNumber) && (
                                    <div title={receipt.duplicateInfo?.phoneNumber || ''}>
                                      {receipt.duplicateInfo?.contactName || receipt.duplicateInfo?.phoneNumber}
                                    </div>
                                  )}
                                  {receipt.duplicateInfo?.phoneNumber && (
                                    <button
                                      type="button"
                                      className="btn-view-image"
                                      style={{ marginTop: 4, padding: '2px 8px', fontSize: 11 }}
                                      onClick={() => handleOpenChat(receipt.duplicateInfo.phoneNumber)}
                                      title="Abrir chat del comprobante original"
                                    >
                                      Ver chat original
                                    </button>
                                  )}
                                </div>
                              ) : <span className="no-id">-</span>}
                            </td>
                            <td>
                              {receipt.appliedBy === 'Diana IA'
                                ? <span className="applied-by-ia">🤖 Diana IA</span>
                                : receipt.appliedBy === 'Manual'
                                  ? <span className="applied-by-manual">👤 Manual</span>
                                  : <span className="applied-by-none">-</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'monthly' && (
            <div className="monthly-tab">
              <div className="month-selector">
                <label>Año:</label>
                <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                <label>Mes:</label>
                <select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <option key={month} value={month}>
                      {new Date(2000, month - 1).toLocaleDateString('es-DO', { month: 'long' })}
                    </option>
                  ))}
                </select>
                <button onClick={loadMonthlyStats}>Actualizar</button>
              </div>

              {monthlyStats && (
                <>
                  <div className="monthly-summary">
                    <h3>Resumen del Mes - {new Date(monthlyStats.year, monthlyStats.month - 1).toLocaleDateString('es-DO', { month: 'long', year: 'numeric' })}</h3>
                    <div className="monthly-stats-grid">
                      <div className="stat-box">
                        <div className="stat-label">Total Recibos</div>
                        <div className="stat-number">{monthlyStats.total}</div>
                      </div>
                      <div className="stat-box success">
                        <div className="stat-label">Aprobados</div>
                        <div className="stat-number">{monthlyStats.approved}</div>
                      </div>
                      <div className="stat-box warning">
                        <div className="stat-label">Pendientes</div>
                        <div className="stat-number">{monthlyStats.pending}</div>
                      </div>
                      <div className="stat-box info">
                        <div className="stat-label">Auto-IA</div>
                        <div className="stat-number">{monthlyStats.autoApplied}</div>
                      </div>
                      <div className="stat-box amount">
                        <div className="stat-label">Monto Total</div>
                        <div className="stat-number">{formatCurrency(monthlyStats.totalAmount)}</div>
                      </div>
                    </div>
                  </div>

                  <div className="monthly-banks-breakdown">
                    <h3>Desglose por Banco (Mes)</h3>
                    <div className="banks-grid">
                      {Object.entries(monthlyStats.byBank).map(([bank, data]) => (
                        <div key={bank} className="bank-card">
                          <h4>{bank}</h4>
                          <div className="bank-stats">
                            <div className="bank-stat">
                              <span className="label">Cantidad:</span>
                              <span className="value">{data.count}</span>
                            </div>
                            <div className="bank-stat">
                              <span className="label">Monto:</span>
                              <span className="value amount">{formatCurrency(data.amount)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="monthly-daily-breakdown">
                    <h3>Desglose Diario</h3>
                    <table className="daily-breakdown-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Total</th>
                          <th>Aprobados</th>
                          <th>Pendientes</th>
                          <th>Auto-IA</th>
                          <th>Monto</th>
                          <th>BANRESERVAS</th>
                          <th>BANCO POPULAR</th>
                          <th>BANCO BHD LEON</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyStats.daily.map(day => (
                          <tr key={day.date}>
                            <td>{formatDateShortRD(day.date)}</td>
                            <td>{day.total}</td>
                            <td className="success">{day.approved}</td>
                            <td className="warning">{day.pending}</td>
                            <td className="info">{day.autoApplied}</td>
                            <td className="amount">{formatCurrency(day.totalAmount)}</td>
                            <td>
                              {day.byBank.BANRESERVAS.count} ({formatCurrency(day.byBank.BANRESERVAS.amount)})
                            </td>
                            <td>
                              {day.byBank['BANCO POPULAR'].count} ({formatCurrency(day.byBank['BANCO POPULAR'].amount)})
                            </td>
                            <td>
                              {day.byBank['BANCO BHD LEON'].count} ({formatCurrency(day.byBank['BANCO BHD LEON'].amount)})
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'pending' && (
            <div className="pending-tab">
              <div className="pending-header">
                <h3>Recibos Pendientes de Revisión ({pendingReceipts.length})</h3>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button 
                    onClick={() => setShowRegisterPayment(true)}
                    style={{
                      padding: '10px 20px',
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    ➕ Registrar Pago Manual
                  </button>
                  <button onClick={loadPendingReceipts}>Actualizar</button>
                </div>
              </div>

              <table className="receipts-table">
                <thead>
                  <tr>
                    <th>Fecha Recepción</th>
                    <th>ID</th>
                    <th>Cliente</th>
                    <th>Banco</th>
                    <th>Tipo</th>
                    <th>Monto</th>
                    <th>Origen</th>
                    <th>Nº Trans.</th>
                    <th>Fecha Recibo</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingReceipts.map(receipt => (
                    <tr key={receipt._id} className={receipt.originAlert ? 'origin-alert-row' : ''}>
                      <td>{formatDateTimeRD(receipt.createdAt, { hour: '2-digit', minute: '2-digit' })}</td>
                      <td>
                        {receipt.contact?.mikrowispId
                          ? <span className="client-id-link" onClick={() => handleOpenChat(receipt.phoneNumber)} title="Abrir chat">{receipt.contact.mikrowispId}</span>
                          : <span className="no-id">-</span>}
                      </td>
                      <td>
                        <span className="client-name-link" onClick={() => handleOpenChat(receipt.phoneNumber)} title="Abrir chat">
                          {receipt.contact?.name || receipt.phoneNumber}
                        </span>
                      </td>
                      <td>{receipt.bank || 'N/A'}</td>
                      <td>{receipt.type || 'N/A'}</td>
                      <td className="amount">{formatCurrency(receipt.amount)}</td>
                      <td>
                        {(receipt.accountNumber || receipt.phoneNumber || receipt.verificationResult?.sourceAccount) ? (
                          <span className="origin-cell">
                            <span className="origin-value" title="Cuenta origen o teléfono que envió">
                              {receipt.accountNumber || receipt.verificationResult?.sourceAccount || receipt.phoneNumber}
                            </span>
                            {receipt.originAlert && (
                              <span className="origin-alert-badge" title="⚠️ Este origen envió otro recibo en los últimos 15 días">⚠️</span>
                            )}
                          </span>
                        ) : <span className="no-id">-</span>}
                      </td>
                      <td className="mono-small">
                        {receipt.transactionNumber || receipt.confirmationNumber || receipt.verificationResult?.transactionNumber || receipt.verificationResult?.confirmationNumber || receipt.verificationResult?.referenceNumber || <span className="no-id">-</span>}
                      </td>
                      <td>{receipt.date ? formatDateShortRD(receipt.date) : 'N/A'}</td>
                      <td>
                        <span className={`status-badge ${receipt.status}`}>
                          {statusLabel(receipt.status)}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <a
                            href={`/api/receipts/image/${receipt._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-view-image"
                          >
                            🖼️ Ver
                          </a>
                          {receipt.status !== 'approved' && (
                            <button
                              onClick={() => {
                                setApplyingReceipt(receipt._id);
                                setApplyPaymentData({
                                  amount: receipt.amount || '',
                                  bank: receipt.bank || 'BANRESERVAS'
                                });
                              }}
                              style={{
                                padding: '4px 10px',
                                background: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '600'
                              }}
                            >
                              💳 Aplicar
                            </button>
                          )}
                        </div>
                        {applyingReceipt === receipt._id && (
                          <div style={{
                            marginTop: '8px', padding: '10px', background: '#f8f9fa',
                            borderRadius: '6px', border: '1px solid #dee2e6', minWidth: '220px'
                          }}>
                            <div style={{ marginBottom: '6px', fontWeight: '600', fontSize: '12px' }}>Aplicar Pago Manual</div>
                            <div style={{ display: 'flex', gap: '6px', marginBottom: '6px', flexWrap: 'wrap' }}>
                              <input
                                type="number"
                                step="0.01"
                                placeholder="Monto RD$"
                                value={applyPaymentData.amount}
                                onChange={(e) => setApplyPaymentData({ ...applyPaymentData, amount: e.target.value })}
                                style={{ width: '100px', padding: '4px 6px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '12px' }}
                              />
                              <select
                                value={applyPaymentData.bank}
                                onChange={(e) => setApplyPaymentData({ ...applyPaymentData, bank: e.target.value })}
                                style={{ padding: '4px 6px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '12px' }}
                              >
                                <option value="BANRESERVAS">BANRESERVAS</option>
                                <option value="BANCO POPULAR">BANCO POPULAR</option>
                                <option value="BANCO BHD LEON">BHD LEON</option>
                                <option value="EFECTIVO">EFECTIVO</option>
                              </select>
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                onClick={() => handleApplyPending(receipt)}
                                disabled={loading}
                                style={{
                                  padding: '4px 12px', background: '#007bff', color: 'white',
                                  border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                                }}
                              >
                                {loading ? '...' : '✅ Confirmar'}
                              </button>
                              <button
                                onClick={() => setApplyingReceipt(null)}
                                style={{
                                  padding: '4px 10px', background: '#6c757d', color: 'white',
                                  border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                                }}
                              >
                                Cancelar
                              </button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'auto-applied' && (
            <div className="auto-applied-tab">
              <div className="date-selector">
                <label>Fecha:</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
                <button onClick={loadAutoAppliedReceipts}>Actualizar</button>
              </div>

              <div className="auto-applied-header">
                <h3>🤖 Recibos Aplicados Automáticamente por IA</h3>
                <span className="auto-applied-count">{autoAppliedReceipts.length} recibos</span>
              </div>

              {autoAppliedReceipts.length === 0 && !loading && (
                <div className="no-data-message">No hay recibos auto-aplicados para esta fecha.</div>
              )}

              <div className="auto-applied-cards">
                {autoAppliedReceipts.map(receipt => (
                  <div key={receipt._id} className="auto-applied-card">
                    {/* Encabezado de tarjeta */}
                    <div className="aac-header">
                      <div className="aac-client">
                        <span className="aac-icon">👤</span>
                        <div>
                          <div className="aac-client-name">{receipt.contact?.name || receipt.phoneNumber}</div>
                          <div className="aac-phone">{receipt.phoneNumber}</div>
                        </div>
                      </div>
                      <div className="aac-badges">
                        <span className="aac-badge success">✅ Auto-Aplicado</span>
                        {receipt.contact?.mikrowispId && (
                          <span className="aac-badge mikrowisp">🔗 ID: {receipt.contact.mikrowispId}</span>
                        )}
                      </div>
                    </div>

                    {/* Cuerpo de tarjeta: dos columnas */}
                    <div className="aac-body">
                      <div className="aac-col">
                        <div className="aac-field">
                          <span className="aac-label">🏦 Banco</span>
                          <span className="aac-value">{receipt.bank || 'N/A'}</span>
                        </div>
                        {receipt.detectedBank && receipt.detectedBank !== receipt.bank && (
                          <div className="aac-field">
                            <span className="aac-label">🤖 Banco IA</span>
                            <span className="aac-value dim">{receipt.detectedBank}</span>
                          </div>
                        )}
                        <div className="aac-field">
                          <span className="aac-label">💰 Monto</span>
                          <span className="aac-value amount">{formatCurrency(receipt.amount)}</span>
                        </div>
                        {receipt.detectedAmount && Number(receipt.detectedAmount) !== Number(receipt.amount) && (
                          <div className="aac-field">
                            <span className="aac-label">🤖 Monto IA</span>
                            <span className="aac-value dim">{formatCurrency(receipt.detectedAmount)}</span>
                          </div>
                        )}
                        <div className="aac-field">
                          <span className="aac-label">📋 Tipo</span>
                          <span className="aac-value">{receipt.type || 'N/A'}</span>
                        </div>
                      </div>

                      <div className="aac-col">
                        {receipt.ownerName && (
                          <div className="aac-field">
                            <span className="aac-label">🪪 Nombre en Recibo</span>
                            <span className="aac-value highlight">{receipt.ownerName}</span>
                          </div>
                        )}
                        {receipt.confirmationNumber && (
                          <div className="aac-field">
                            <span className="aac-label">🔢 Nº Confirmación</span>
                            <span className="aac-value mono">{receipt.confirmationNumber}</span>
                          </div>
                        )}
                        {receipt.transactionNumber && receipt.transactionNumber !== receipt.confirmationNumber && (
                          <div className="aac-field">
                            <span className="aac-label">🔢 Nº Transacción</span>
                            <span className="aac-value mono">{receipt.transactionNumber}</span>
                          </div>
                        )}
                        {receipt.accountNumber && (
                          <div className="aac-field">
                            <span className="aac-label">🏦 Origen</span>
                            <span className={`aac-value mono${receipt.originAlert ? ' origin-alert-text' : ''}`}>
                              {receipt.accountNumber}
                              {receipt.originAlert && (
                                <span className="origin-alert-badge" title="⚠️ Mismo origen envió otro recibo en los últimos 15 días"> ⚠️</span>
                              )}
                            </span>
                          </div>
                        )}
                        <div className="aac-field">
                          <span className="aac-label">📅 Fecha Recibo</span>
                          <span className="aac-value">{receipt.date ? formatDateShortRD(receipt.date) : 'N/A'}{receipt.time ? ' ' + receipt.time : ''}</span>
                        </div>
                        <div className="aac-field">
                          <span className="aac-label">🕐 Recibido</span>
                          <span className="aac-value">{formatDateTimeRD(receipt.createdAt, { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </div>
                    </div>

                    {/* Pie de tarjeta */}
                    <div className="aac-footer">
                      <div className="aac-field inline">
                        <span className="aac-label">🧾 Factura Mikrowisp</span>
                        <span className="aac-value invoice-id">{receipt.appliedPaymentId || 'N/A'}</span>
                      </div>
                      {receipt.isAuthentic !== null && (
                        <span className={`aac-badge ${receipt.isAuthentic ? 'success' : 'danger'}`}>
                          {receipt.isAuthentic ? '✅ Auténtico' : '⚠️ No verificado'}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Modal para registrar pago manual */}
          {showRegisterPayment && (
            <div className="register-payment-modal-overlay" onClick={() => setShowRegisterPayment(false)}>
              <div className="register-payment-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Registrar Pago Manual</h3>
                  <button className="close-button" onClick={() => setShowRegisterPayment(false)}>✕</button>
                </div>
                <div className="modal-content">
                  <div className="form-group">
                    <label>Teléfono del Cliente *</label>
                    <input
                      type="text"
                      value={registerPaymentData.phoneNumber}
                      onChange={(e) => setRegisterPaymentData({ ...registerPaymentData, phoneNumber: e.target.value })}
                      placeholder="8091234567"
                    />
                  </div>
                  <div className="form-group">
                    <label>Monto *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={registerPaymentData.amount}
                      onChange={(e) => setRegisterPaymentData({ ...registerPaymentData, amount: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="form-group">
                    <label>Banco *</label>
                    <select
                      value={registerPaymentData.bank}
                      onChange={(e) => setRegisterPaymentData({ ...registerPaymentData, bank: e.target.value })}
                    >
                      <option value="BANRESERVAS">BANRESERVAS</option>
                      <option value="BANCO POPULAR">BANCO POPULAR</option>
                      <option value="BANCO BHD LEON">BANCO BHD LEON</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Tipo *</label>
                    <select
                      value={registerPaymentData.type}
                      onChange={(e) => setRegisterPaymentData({ ...registerPaymentData, type: e.target.value })}
                    >
                      <option value="transferencia">Transferencia</option>
                      <option value="deposito">Depósito</option>
                      <option value="sucursal">Sucursal</option>
                      <option value="cajero">Cajero</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Fecha del Recibo *</label>
                    <input
                      type="date"
                      value={registerPaymentData.date}
                      onChange={(e) => setRegisterPaymentData({ ...registerPaymentData, date: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Hora (opcional)</label>
                    <input
                      type="time"
                      value={registerPaymentData.time}
                      onChange={(e) => setRegisterPaymentData({ ...registerPaymentData, time: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Número de Transferencia (opcional)</label>
                    <input
                      type="text"
                      value={registerPaymentData.transactionNumber}
                      onChange={(e) => setRegisterPaymentData({ ...registerPaymentData, transactionNumber: e.target.value })}
                      placeholder="Número de confirmación o transacción"
                    />
                  </div>
                  <div className="form-group">
                    <label>Notas (opcional)</label>
                    <textarea
                      value={registerPaymentData.notes}
                      onChange={(e) => setRegisterPaymentData({ ...registerPaymentData, notes: e.target.value })}
                      rows="3"
                      placeholder="Notas adicionales sobre el pago"
                    />
                  </div>
                  <div className="modal-actions">
                    <button
                      className="btn-cancel"
                      onClick={() => setShowRegisterPayment(false)}
                    >
                      Cancelar
                    </button>
                    <button
                      className="btn-save"
                      onClick={async () => {
                        try {
                          setLoading(true);
                          const response = await axios.post('/api/receipts/register-manual', registerPaymentData, {
                            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                          });
                          
                          if (response.data.success) {
                            alert('✅ Pago registrado exitosamente');
                            setShowRegisterPayment(false);
                            setRegisterPaymentData({
                              phoneNumber: '',
                              amount: '',
                              bank: 'BANRESERVAS',
                              type: 'transferencia',
                              date: new Date().toISOString().split('T')[0],
                              time: '',
                              transactionNumber: '',
                              notes: ''
                            });
                            loadPendingReceipts();
                            loadSummary();
                          }
                        } catch (error) {
                          console.error('Error registrando pago:', error);
                          alert('Error al registrar el pago: ' + (error.response?.data?.error || error.message));
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={!registerPaymentData.phoneNumber || !registerPaymentData.amount || !registerPaymentData.date || loading}
                    >
                      {loading ? 'Guardando...' : 'Guardar Pago'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReceiptsDashboard;

